import { LitElement, html } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import {
  fs24User,
  fs24UserFilled,
  fs24UserMultiuser,
  fs24UserMultiuserFilled,
} from '@cof/fs-icons';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { ApplicationService } from '../../../../services/application.service.js';
import '@cof/fs-radio-wc/fs-radio.js';
import { style } from './toggle-coborrower.style.js';
import { globalStyle } from '../../../../global-styles.style.js';
import { stratum } from '../../../../../constants/stratum.js';

export class RefiPersonalToggleCoborrowerComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiPersonalToggleCoborrower');
    this.loading = false;
    this.isIconic = false;

    const icons = [
      fs24User,
      fs24UserFilled,
      fs24UserMultiuser,
      fs24UserMultiuserFilled,
    ];
    for (const icon of icons) {
      FsIcon.add(icon);
    }
  }

  static properties = {
    loading: { type: Boolean, reflect: true },
    isIconic: { type: Boolean, attribute: 'is-iconic', reflect: true },
    applicationType: { type: String, reflect: true },
  };

  static styles = [style, globalStyle];

  connectedCallback() {
    super.connectedCallback();
    stratum.publishTag(
      TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_COBORROWER
    );
    this.applicationType = ApplicationService.Application.applicationType;
    this.addCustomPadding();
  }

  renderIconicV2() {
    return html` <fs-choice-button-group
      direction="row"
      field-label="Add a co-borrower?"
    >
      <fs-choice-button
        id="toggleCoborrowerNo"
        name="toggleCoborrower"
        type="radio"
        alignment="center"
        .labelText="${html`<span>Apply</span><br /><span>by myself</span>`}"
        value="INDIVIDUAL"
        ?selected="${this.applicationType === 'INDIVIDUAL' ||
        !this.applicationType}"
        data-e2e-ref="iconicToggleCoborrowerNo"
        @click="${this.applicationTypeChange}"
        @blur="${(ev) => {
          if (ev.target.validity.valid) {
            stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
              replacements: { value: 'iconicToggleCoborrowerNo' },
            });
          } else {
            stratum.publishTag(
              TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
              { replacements: { value: 'This is required' } }
            );
          }
        }}"
      >
        ${this.applicationType === 'INDIVIDUAL'
          ? html`<fs-icon
              slot="choice-button-icon"
              icon="fs-24-user-filled"
            ></fs-icon>`
          : html`<fs-icon
              slot="choice-button-icon"
              icon="fs-24-user"
            ></fs-icon>`}
      </fs-choice-button>

      <fs-choice-button
        id="toggleCoborrowerYes"
        name="toggleCoborrower"
        type="radio"
        alignment="center"
        .labelText="${html`<span>Apply with</span><br /><span
            >a co-borrower</span
          >`}"
        value="JOINT"
        ?selected="${this.applicationType === 'JOINT'}"
        data-e2e-ref="iconicToggleCoborrowerYes"
        @click="${this.applicationTypeChange}"
        @blur="${(ev) => {
          if (ev.target.validity.valid) {
            stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
              replacements: { value: 'iconicToggleCoborrowerYes' },
            });
          } else {
            stratum.publishTag(
              TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
              { replacements: { value: 'This is required' } }
            );
          }
        }}"
      >
        ${this.applicationType === 'JOINT'
          ? html`<fs-icon
              slot="choice-button-icon"
              icon="fs-24-user-multiuser-filled"
            ></fs-icon>`
          : html`<fs-icon
              slot="choice-button-icon"
              icon="fs-24-user-multiuser"
            ></fs-icon>`}
      </fs-choice-button>
    </fs-choice-button-group>`;
  }

  render() {
    if (this.isIconic) {
      return this.renderIconicV2();
    }
    return html`
      <div role="radiogroup" aria-labelledby="toggleCoborrowerHeader">
        <fs-body-text size="normal" id="toggleCoborrowerHeader"
          >Want to add a co-applicant?</fs-body-text
        >

        <div>
          <fs-radio
            id="toggleCoborrowerYes"
            name="toggleCoborrower"
            value="JOINT"
            required
            ?checked="${this.applicationType === 'JOINT'}"
            @change="${this.applicationTypeChange}"
            @blur="${(ev) => {
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: { value: 'toggleCoborrowerYes' },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  { replacements: { value: 'This is required' } }
                );
              }
            }}"
            >Yes
          </fs-radio>
        </div>
        <div>
          <fs-radio
            id="toggleCoborrowerNo"
            name="toggleCoborrower"
            value="INDIVIDUAL"
            required
            ?checked="${this.applicationType !== 'JOINT'}"
            @change="${this.applicationTypeChange}"
            @blur="${(ev) => {
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: { value: 'toggleCoborrowerNo' },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  { replacements: { value: 'This is required' } }
                );
              }
            }}"
            >No
          </fs-radio>
        </div>
      </div>
    `;
  }

  firstUpdated() {
    this.application = ApplicationService.Application;
  }

  addCustomPadding() {
    setTimeout(() => {
      if (this.isIconic) {
        const toggleYes = this.shadowRoot.getElementById('toggleCoborrowerYes');
        const toggleNo = this.shadowRoot.getElementById('toggleCoborrowerNo');

        if (toggleNo) {
          toggleNo.renderRoot?.children[1]?.setAttribute(
            'style',
            'padding:1rem'
          );
        }
        if (toggleYes) {
          toggleYes.renderRoot?.children[1]?.setAttribute(
            'style',
            'padding:1rem'
          );
        }
      }
    }, 0);
  }

  applicationTypeChange(ev) {
    this.applicationType = ev.target.value;
    this.dispatchEvent(
      new CustomEvent('toggle-coborrower', {
        detail: {
          applicationType: this.applicationType,
        },
      })
    );
  }
}

customElements.define(
  'refi-toggle-coborrower',
  RefiPersonalToggleCoborrowerComponent
);
